import { useEffect, useRef } from 'react';

import { TUseComposerAutoFocusParams } from '@/components/molecule/ChatInput/types';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { consumeComposerFocusRequest, selectIsComposerFocusRequested } from '@/store/composerFocus/composerFocusSlice';

const DIALOG_SELECTOR = '[role="dialog"]';

/**
 * Owns keyboard focus for the composer. It holds at most one pending claim on the caret at a time and
 * places the caret as soon as the composer is usable and free to take it. A claim comes from one of
 * two places:
 *
 * - Arrival: raised once per mount, the first time the composer becomes usable. It has no mandate to
 *   take focus from anyone, so it is only honoured while nothing else holds focus.
 * - An explicit request in the store: raised by an action whose entire intent is to start typing
 *   ("add new chat"). The request is that mandate, so it overrides whatever holds focus — normally
 *   the control the user just activated.
 *
 * Both kinds of claim defer while a modal dialog is open, so the composer never pulls focus out of a
 * dialog's focus trap, and both survive until the composer can act on them. The composer is
 * read-only rather than disabled while an answer is in flight, so focus is never stranded on the
 * document body.
 *
 * It also hands the caret to the textarea when a send starts from the send button.
 */
const useComposerAutoFocus = ({ textAreaRef, sendButtonRef, isDisabled }: TUseComposerAutoFocusParams) => {
  const dispatch = useAppDispatch();
  const isFocusRequested = useAppSelector(selectIsComposerFocusRequested);
  const hasClaimedArrivalFocusRef = useRef(false);
  const wasBlockedByDialogRef = useRef(false);

  useEffect(() => {
    if (isDisabled) return;

    /** Whether any claim on the caret is still outstanding. */
    const isClaimPending = () => !hasClaimedArrivalFocusRef.current || isFocusRequested;

    /**
     * Moves the caret into the composer if a claim is pending and nothing blocks it, consuming the
     * explicit request when that is what was honoured. Returns whether focus was claimed.
     */
    const claimFocus = (): boolean => {
      if (!isClaimPending()) return false;

      if (document.querySelector(DIALOG_SELECTOR) !== null) {
        wasBlockedByDialogRef.current = true;
        return false;
      }

      // INFO: Only the arrival claim yields to whoever holds focus. An explicit request is the user
      // stating they are about to type, so it is honoured even though their click left focus on the
      // control that raised it.
      const isFocusUnclaimed = document.activeElement === null || document.activeElement === document.body;
      if (!isFocusRequested && !isFocusUnclaimed) return false;

      hasClaimedArrivalFocusRef.current = true;
      textAreaRef.current?.focus();
      // INFO: Consumed at the moment the caret lands, not in a separate effect, so the request cannot
      // be honoured twice or cleared before it was acted on.
      if (isFocusRequested) dispatch(consumeComposerFocusRequest());
      return true;
    };

    if (claimFocus() || !isClaimPending() || !wasBlockedByDialogRef.current) return;

    // INFO: The chat page can mount behind a modal (release notes, onboarding) that owns focus and
    // stays in the DOM until its closing animation ends, so a blocked claim waits for the dialog to
    // be removed. Watching stops as soon as the caret is placed.
    const observer = new MutationObserver(() => {
      if (claimFocus()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
    // INFO: isDisabled gates every claim — the chat page can mount while a thread is still being
    // restored, so a claim waits until the composer accepts input. isFocusRequested is the explicit
    // claim being raised. dispatch is stable and textAreaRef is a stable ref object.
  }, [isDisabled, isFocusRequested, dispatch, textAreaRef]);

  useEffect(() => {
    if (!isDisabled) return;

    // INFO: The send button turns inoperable for the duration of the request, so the caret is handed
    // to the composer as the send starts — it stays focusable while busy (read-only, not disabled),
    // which keeps the focus ring off a control that cannot be used and leaves the user where they
    // continue typing. Focus anywhere else belongs to the user and is left untouched.
    if (document.activeElement === sendButtonRef.current) textAreaRef.current?.focus();
    // INFO: isDisabled is the only trigger — it turns true exactly when the question is sent. Both
    // refs are stable ref objects and never change identity.
  }, [isDisabled, textAreaRef, sendButtonRef]);
};

export { useComposerAutoFocus };
